/**
 * Domain Analysis — Insights Page
 *
 * Answers: "How did each domain score, and what does that mean
 * for our compliance and risk posture?"
 */

import { useState } from 'react'
import { useInsightsData } from '../hooks/useInsightsData'
import InsightCard from '../components/InsightCard'
import FrameworkCoverageTable from '../components/FrameworkCoverageTable'
import { getMaturityLevelByScore } from '../../../shared/utils/maturityLevels'

function DomainAnalysis({ assessmentId, client, embedded = false }) {
  const { data, raw, loading, error } = useInsightsData(assessmentId)
  const [expandedDomain, setExpandedDomain] = useState(null)
  const [sortBy, setSortBy] = useState('score-asc')

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>
  if (error || !data) return <div className="text-center py-12 text-gray-500 dark:text-gray-400">Unable to load domain analysis.</div>

  const { domainAnalysis } = data
  const categories = [...(domainAnalysis.categories || [])]

  // Sort
  if (sortBy === 'score-asc') categories.sort((a, b) => a.score - b.score)
  else if (sortBy === 'score-desc') categories.sort((a, b) => b.score - a.score)
  else if (sortBy === 'name') categories.sort((a, b) => a.name.localeCompare(b.name))

  const avgScore = categories.length > 0 ? Math.round(categories.reduce((s, c) => s + c.score, 0) / categories.length) : 0
  const strongest = categories.length > 0 ? [...categories].sort((a, b) => b.score - a.score)[0] : null
  const weakest = categories.length > 0 ? [...categories].sort((a, b) => a.score - b.score)[0] : null

  // Framework coverage from raw data
  const fwCoverage = raw?.frameworkCoverage

  return (
    <div className={`${embedded ? '' : 'px-1 py-2'} space-y-5`}>
      {/* Header — hidden when embedded in Program Overview */}
      {!embedded && (
      <div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Domain Analysis</h1>
        <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5">
          Detailed maturity breakdown across {categories.length} security domains
        </p>
      </div>
      )}

      {/* Summary bar */}
      <div className="flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400">
        <span><strong className="text-gray-900 dark:text-white">{categories.length}</strong> domains assessed</span>
        <span>Average: <strong className="text-gray-900 dark:text-white">{avgScore}%</strong></span>
        {strongest && <span>Strongest: <strong className="text-green-600 dark:text-green-400">{strongest.name} ({strongest.score}%)</strong></span>}
        {weakest && <span>Weakest: <strong className="text-red-600 dark:text-red-400">{weakest.name} ({weakest.score}%)</strong></span>}
      </div>

      {/* Sort controls */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500 dark:text-gray-400">Sort:</span>
        {[
          { value: 'score-asc', label: 'Score (low → high)' },
          { value: 'score-desc', label: 'Score (high → low)' },
          { value: 'name', label: 'Alphabetical' }
        ].map(opt => (
          <button
            key={opt.value}
            onClick={() => setSortBy(opt.value)}
            className={`px-2.5 py-1 text-xs rounded-md transition-colors ${sortBy === opt.value ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white font-medium' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Domain list */}
      <div className="bg-white dark:bg-gray-800/50 rounded-xl border border-gray-200/60 dark:border-gray-700/40 divide-y divide-gray-100 dark:divide-gray-700/30">
        {categories.map(cat => {
          const color = getMaturityLevelByScore(cat.score).color
          const isExpanded = expandedDomain === cat.id

          return (
            <div key={cat.id}>
              <button
                onClick={() => setExpandedDomain(isExpanded ? null : cat.id)}
                className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left"
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="w-1 h-8 rounded-full flex-shrink-0" style={{ borderLeft: `3px solid ${color}` }} />
                  <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{cat.name}</span>
                </div>
                <div className="flex items-center gap-4 flex-shrink-0">
                  <div className="w-24">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${cat.score}%`, backgroundColor: color }} />
                      </div>
                      <span className="text-sm font-semibold" style={{ color }}>{cat.score}%</span>
                    </div>
                  </div>
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: `${color}1F`, color }}>
                    {cat.maturityLevel?.label || 'N/A'}
                  </span>
                  <svg className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {isExpanded && (
                <div className="px-5 pb-4 pt-1 border-t border-gray-50 dark:border-gray-700/20">
                  <div className="ml-4 space-y-2 text-sm">
                    <div className="flex gap-8">
                      <div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">Questions answered</span>
                        <p className="text-gray-900 dark:text-white font-medium">{cat.answeredCount || cat.questionCount}/{cat.questionCount}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">Maturity level</span>
                        <p className="font-medium" style={{ color }}>{cat.maturityLevel?.label} (Level {cat.maturityLevel?.level})</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Framework coverage intersection */}
      {fwCoverage && Object.keys(fwCoverage.frameworks).length > 0 && (
        <InsightCard title="Framework Coverage Intersection" subtitle="How assessment domains map to applicable regulatory frameworks" accent="#378ADD">
          <FrameworkCoverageTable frameworks={fwCoverage.frameworks} disclaimer={fwCoverage.summary?.assessmentCoverageDisclaimer} />
        </InsightCard>
      )}
    </div>
  )
}

export default DomainAnalysis
